/* eslint-disable jsx-a11y/no-redundant-roles */
import React, { useRef } from 'react';
import { Stack } from '../Stack';
import { Text } from '../Text';
import { generateTableCss } from './utils';
import { useCellWidths, useColumns, useDragToScroll, useScrollPosition, useSelection, } from './hooks';
import { renderCell, renderFooter, renderHeader, renderLoadingCell, renderNestedCell, } from './renderers';
import { ActionBar } from './components/ActionBar';
import { Cell } from './components/Cell';
import { ColumnMenu } from './components/ColumnMenu';
import { EmptyBodyContent, EmptyBodyGlyphWrapper } from './components/EmptyBodyContent';
import { Header } from './components/Header';
import { NoWrap } from './components/NoWrap';
import { Row } from './components/Row';
import { ScrollContainer } from './components/ScrollContainer';
import { TableGrid } from './components/TableGrid';
import { Truncate } from './components/Truncate';
import { Wrapper } from './components/Wrapper';
import { SelectionCell } from './components/SelectionCell';
import { SelectionHeader } from './components/SelectionHeader';
import { StickyHead } from './components/StickyHead';
import { getRowState } from './utils/getRowState';
import { InfoIcon } from '../../tempIcons/InfoIcon';
const defaultEmptyState = {
    icon: React.createElement(InfoIcon, { width: 48, height: 48 }),
    heading: 'No results',
};
const emptyAction = () => { };
export const DataTableExp = ({ ActionBarSlot, borderMode, columns, emptyState = defaultEmptyState, expandedRowIds, loading, loadingRowCount, onChangeSort, rows, disabledRowIds, selectionMode, selectionState, setExpanded, sortState, stripingMode, enableStickyHeader, }) => {
    // Refs
    const scrollContainerRef = useRef(null);
    const tableRef = useRef(null);
    const intersectionTargetRef = useRef(null);
    useDragToScroll(scrollContainerRef);
    // Configure row selection
    const enabledRowIds = rows.map((row) => row.id).filter((id) => !(disabledRowIds === null || disabledRowIds === void 0 ? void 0 : disabledRowIds.includes(id)));
    const { selectionEnabled, selectedRowIds, selectionColumn, setSelected } = useSelection({
        selectionMode,
        selectionState,
        enabledRowIds,
    });
    // Assign actions
    const actions = {
        setSelected: setSelected || emptyAction,
        setExpanded: setExpanded || emptyAction,
    };
    // Process columns
    const columnsWithSelection = [selectionColumn, ...columns];
    const allColumns = selectionEnabled ? columnsWithSelection : columns;
    const cellWidths = useCellWidths(tableRef, scrollContainerRef);
    const { visible, pinnedLeft, pinnedRight } = useColumns(allColumns);
    // Generate styles
    const { scrollPosition } = useScrollPosition(scrollContainerRef);
    const tableCss = generateTableCss({
        pinnedLeft,
        pinnedRight,
        cellWidths,
        scrollPosition,
        visible,
        stripingMode,
    });
    // Render states
    if (loading) {
        const loadingRows = new Array(loadingRowCount).fill(true).map(() => ({
            // generate random ID forcing all rows to re-render avoid animation de-sync
            id: Math.random() * 1000,
            data: null,
        }));
        return (React.createElement(Wrapper, { borderMode: borderMode },
            React.createElement(ScrollContainer, { ref: scrollContainerRef },
                React.createElement(TableGrid, { css: tableCss, ref: tableRef, role: "table" },
                    React.createElement("thead", { role: "rowgroup" },
                        React.createElement(Row, { role: "row" }, visible.map((column) => renderHeader({ column, sortState, onChangeSort })))),
                    React.createElement("tbody", { role: "rowgroup" }, loadingRows.map((row) => (React.createElement(Row, { key: row.id }, visible.map((col) => renderLoadingCell(row, col))))))))));
    }
    if (rows.length === 0) {
        return (React.createElement(Wrapper, { borderMode: borderMode },
            React.createElement(ScrollContainer, { ref: scrollContainerRef },
                React.createElement(TableGrid, { css: tableCss, ref: tableRef, role: "table" },
                    React.createElement("thead", { role: "rowgroup" },
                        React.createElement(Row, { role: "row" }, visible.map((column) => renderHeader({ column, sortState, onChangeSort })))),
                    React.createElement("tbody", { role: "rowgroup" },
                        React.createElement(EmptyBodyContent, { columnCount: visible.length },
                            React.createElement(EmptyBodyGlyphWrapper, null, emptyState.icon),
                            React.createElement(Stack, { spacing: "sm", alignX: "center" },
                                React.createElement(Text, { variant: "headingMedium" }, emptyState.heading),
                                emptyState.subheading && (React.createElement(Text, { variant: "subheadingMedium" }, emptyState.subheading)))))))));
    }
    const renderRow = (row) => {
        const { e2eClassName = '', accentColor } = row;
        const rowState = getRowState(row.id, { selectedRowIds, expandedRowIds, disabledRowIds });
        return (React.createElement(Row, Object.assign({ role: "row", key: row.id, accentColor: accentColor, className: e2eClassName }, rowState), visible.map((col) => renderCell(row, col, actions, rowState))));
    };
    const renderNestedRow = (nestedRow, rowId) => {
        var _a, _b;
        const nestedRowE2eClassName = (_b = (_a = nestedRow === null || nestedRow === void 0 ? void 0 : nestedRow.data) === null || _a === void 0 ? void 0 : _a.e2eClassNames) === null || _b === void 0 ? void 0 : _b.row;
        const rowState = getRowState(rowId, { selectedRowIds, expandedRowIds, disabledRowIds });
        return (React.createElement(Row, { role: "row", key: nestedRow.id, className: nestedRowE2eClassName }, visible.map((col) => renderNestedCell(nestedRow, col, rowState))));
    };
    const renderSection = (row) => {
        var _a;
        const isExpanded = expandedRowIds === null || expandedRowIds === void 0 ? void 0 : expandedRowIds.includes(row.id);
        return (React.createElement("tbody", { role: "rowgroup", key: row.id },
            renderRow(row),
            isExpanded && ((_a = row.nestedRows) === null || _a === void 0 ? void 0 : _a.map((nestedRow) => renderNestedRow(nestedRow, row.id)))));
    };
    const renderBody = () => {
        const hasNestedRows = !!expandedRowIds;
        const hasMultipleSections = hasNestedRows && stripingMode === 'nested';
        if (hasMultipleSections) {
            return rows.map(renderSection);
        }
        if (hasNestedRows) {
            return (React.createElement("tbody", { role: "rowgroup" }, rows.map((row) => {
                var _a;
                const rowElement = renderRow(row);
                const isExpanded = expandedRowIds === null || expandedRowIds === void 0 ? void 0 : expandedRowIds.includes(row.id);
                const nestedRowElements = (isExpanded &&
                    ((_a = row.nestedRows) === null || _a === void 0 ? void 0 : _a.map((nestedRow) => renderNestedRow(nestedRow, row.id)))) ||
                    [];
                return [rowElement, ...nestedRowElements];
            })));
        }
        return React.createElement("tbody", { role: "rowgroup" }, rows.map(renderRow));
    };
    const headersMarkup = (React.createElement(Row, { role: "row" }, visible.map((column) => renderHeader({ column, sortState, onChangeSort }))));
    const shouldRenderFooter = columns.some((column) => !!column.renderFooter);
    const shouldRenderActionBar = selectedRowIds.length > 0 && !!ActionBarSlot;
    return (React.createElement(Wrapper, { borderMode: borderMode },
        shouldRenderActionBar && (React.createElement(ActionBar, { selectionColumn: selectionColumn, intersectionTargetRef: intersectionTargetRef, containerRef: scrollContainerRef }, ActionBarSlot)),
        React.createElement(ScrollContainer, { ref: scrollContainerRef },
            enableStickyHeader && (React.createElement(StickyHead, { tableCss: tableCss, intersectionTargetRef: intersectionTargetRef, containerRef: scrollContainerRef, cellWidths: cellWidths }, headersMarkup)),
            React.createElement("div", { ref: intersectionTargetRef, style: { height: 0 } }),
            React.createElement(TableGrid, { css: tableCss, ref: tableRef, role: "table" },
                React.createElement("thead", { role: "rowgroup" }, headersMarkup),
                renderBody(),
                shouldRenderFooter && (React.createElement("tfoot", { role: "rowgroup" },
                    React.createElement(Row, { role: "row" }, visible.map((column) => renderFooter({ column })))))))));
};
DataTableExp.defaultProps = {
    loading: false,
    loadingRowCount: 5,
    selectionMode: 'multiple',
    stripingMode: 'row',
    enableStickyHeader: true,
};
DataTableExp.Cell = Cell;
DataTableExp.ColumnMenu = ColumnMenu;
DataTableExp.SelectionCell = SelectionCell;
DataTableExp.SelectionHeader = SelectionHeader;
DataTableExp.Header = Header;
DataTableExp.Truncate = Truncate;
DataTableExp.NoWrap = NoWrap;
//# sourceMappingURL=DataTable.js.map